import { useEffect, useState } from "react";
import { X, Plus, Loader } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { addLead, getDiscordLeadCategories, type AddLeadRequest } from "../services/LeadsService";
import type { StoreProductResult } from "../services/ProductSearchService";
import {
  buildLeadRequest,
  isLeadCategoryInputValid,
  leadRetailerFor,
  resolveLeadCategory,
  type LeadCategoryMode,
} from "./AddToLeadModal.utils";
import { getRetailerConfig, type RetailerType } from "../config/retailers";
import { getProductImageUrl } from "../utils/imageUtils";

interface AddToLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: StoreProductResult | null;
  retailer: string;
}

export default function AddToLeadModal({ isOpen, onClose, product, retailer }: AddToLeadModalProps) {
  const [categoryMode, setCategoryMode] = useState<LeadCategoryMode>("product");
  const [customCategory, setCustomCategory] = useState("");
  const [presetCategory, setPresetCategory] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const leadRetailer = leadRetailerFor(retailer);
  const retailerLabel = getRetailerConfig(leadRetailer as RetailerType)?.displayName ?? leadRetailer;

  const { data: channelCategories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ["discord-lead-categories", leadRetailer],
    queryFn: () => getDiscordLeadCategories(leadRetailer),
    enabled: isOpen && !!product,
    staleTime: 60_000,
  });

  const showChannelCategories = !categoriesLoading && channelCategories.length > 0;

  useEffect(() => {
    if (!isOpen || !product) return;
    if (!showChannelCategories && categoryMode === "preset") {
      setCategoryMode("product");
    }
  }, [isOpen, product, showChannelCategories, categoryMode]);

  useEffect(() => {
    if (!isOpen || categoryMode !== "preset" || !channelCategories.length) return;
    setPresetCategory((p) => p || channelCategories[0]);
  }, [isOpen, categoryMode, channelCategories]);

  if (!isOpen || !product) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isLeadCategoryInputValid(categoryMode, customCategory, presetCategory)) {
      toast.error(categoryMode === "custom" ? "Please enter a custom category" : "Please select a channel category");
      return;
    }

    setIsSubmitting(true);

    try {
      const category = resolveLeadCategory({
        categoryMode,
        product,
        customCategory,
        presetCategory,
      });
      const leadData: AddLeadRequest = buildLeadRequest({ product, category, retailer: leadRetailer });

      await addLead(leadData);
      toast.success(`Product added to ${retailerLabel} leads successfully!`);
      onClose();
    } catch (error) {
      console.error("Error adding product to leads:", error);
      toast.error("Failed to add product to leads. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCategoryMode("product");
      setCustomCategory("");
      setPresetCategory("");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onPointerDown={handleClose}>
      <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl w-full max-w-md" onPointerDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add to {retailerLabel} Leads</h3>
          <button
            onPointerDown={handleClose}
            disabled={isSubmitting}
            className="p-1 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="flex items-start gap-4 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
            {product.image ? (
              <img
                src={getProductImageUrl(product.image, "400")}
                alt={product.name}
                className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-dark-600 flex-shrink-0"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-100 dark:bg-dark-600 rounded-lg border border-gray-200 dark:border-dark-600 flex items-center justify-center flex-shrink-0">
                <Plus className="w-6 h-6 text-gray-400 dark:text-dark-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 dark:text-white text-sm leading-tight">{product.name}</h4>
              <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                UPC: {product.sku} • SKU: {product.barcode}
              </p>
              <p className="text-xs text-gray-500 dark:text-dark-400">MSRP: ${product.msrp?.toFixed(2) || "0.00"}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-3">Category Selection</label>

              {categoriesLoading && (
                <p className="text-xs text-gray-500 dark:text-dark-400 mb-2">Loading channel categories…</p>
              )}

              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 border border-gray-200 dark:border-dark-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                  <input
                    type="radio"
                    name="categoryMode"
                    value="product"
                    checked={categoryMode === "product"}
                    onChange={() => setCategoryMode("product")}
                    className="mt-0.5 text-primary-600 focus:ring-primary-500"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900 dark:text-white">Use Product Category</div>
                    <div className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                      {product.mainCategory || product.categoryPath || "Uncategorized"}
                    </div>
                  </div>
                </label>

                {showChannelCategories && (
                  <label className="flex items-start gap-3 p-3 border border-gray-200 dark:border-dark-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                    <input
                      type="radio"
                      name="categoryMode"
                      value="preset"
                      checked={categoryMode === "preset"}
                      onChange={() => {
                        setCategoryMode("preset");
                        setPresetCategory((prev) => prev || channelCategories[0] || "");
                      }}
                      className="mt-0.5 text-primary-600 focus:ring-primary-500"
                      disabled={isSubmitting}
                    />
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="font-medium text-sm text-gray-900 dark:text-white">Discord channel category</div>
                      <div className="text-xs text-gray-500 dark:text-dark-400">Match a category from your {retailerLabel} lead Discord channels (app config).</div>
                      {categoryMode === "preset" && (
                        <select
                          value={presetCategory}
                          onChange={(e) => setPresetCategory(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
                          disabled={isSubmitting}
                        >
                          {channelCategories.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>
                )}

                <label className="flex items-start gap-3 p-3 border border-gray-200 dark:border-dark-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                  <input
                    type="radio"
                    name="categoryMode"
                    value="custom"
                    checked={categoryMode === "custom"}
                    onChange={() => setCategoryMode("custom")}
                    className="mt-0.5 text-primary-600 focus:ring-primary-500"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900 dark:text-white">Custom Category</div>
                    <div className="text-xs text-gray-500 dark:text-dark-400 mt-1">Enter your own category name</div>
                  </div>
                </label>

                {categoryMode === "custom" && (
                  <div className="ml-6">
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter category name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white dark:placeholder-dark-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      disabled={isSubmitting}
                      maxLength={100}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onPointerDown={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isLeadCategoryInputValid(categoryMode, customCategory, presetCategory)}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add to Leads
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
